import axios from 'axios';
import { StaleDataError } from '../errors/StaleDataError';
import { setSignOutReason } from './sessionEnd';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Token storage helpers ---
const USER_KEY = 'user';

// Access token: in-memory only (NOT localStorage) so an XSS read can't lift it from
// storage at rest. The refresh token lives in an HttpOnly cookie the JS never sees.
let accessTokenInMemory = null;

// Listeners notified whenever the access token changes (login, silent refresh,
// logout). Lets long-lived consumers — notably the WebSocket live-sync client and
// AuthContext's React state — track the rotated token instead of pinning the one
// captured at login.
const accessTokenListeners = new Set();
const onAccessTokenChange = (cb) => { accessTokenListeners.add(cb); return () => accessTokenListeners.delete(cb); };
const notifyAccessTokenChange = (t) => accessTokenListeners.forEach((cb) => { try { cb(t); } catch { /* ignore */ } });

const getAccessToken = () => accessTokenInMemory;
const setTokens = ({ accessToken }) => {
  if (accessToken !== undefined) {
    accessTokenInMemory = accessToken;
    notifyAccessTokenChange(accessToken);
  }
};
const clearTokens = () => {
  accessTokenInMemory = null;
  localStorage.removeItem(USER_KEY);
  notifyAccessTokenChange(null);
};

// Access tokens are short-lived (30 min server-side). Refresh this many ms before
// the JWT `exp` so a token is never handed to a new connection on the edge of expiry.
const TOKEN_REFRESH_SKEW_MS = 30_000;

/** Decode a JWT's `exp` (ms epoch) without verifying the signature. Returns null if unreadable. */
const decodeJwtExpMs = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

/** True when the token has comfortable life left (or its exp can't be read — let the server decide). */
const isAccessTokenFresh = (token) => {
  const expMs = decodeJwtExpMs(token);
  if (expMs == null) return true;
  return Date.now() < expMs - TOKEN_REFRESH_SKEW_MS;
};

// Request interceptor: attach access token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// --- Refresh dance: single-flight ---
// One in-flight refresh shared by every caller (the 401 interceptor AND the
// WebSocket token provider). Refresh tokens rotate on use, so two concurrent
// /auth/refresh calls would race and invalidate each other — this guarantees
// exactly one network call regardless of how many callers ask at once.
let refreshPromise = null;

/**
 * Exchange the refresh cookie for a fresh access token (single-flight).
 * The cookie is sent automatically by the browser (withCredentials: true).
 * On success the new access token is stored in memory and listeners are notified.
 * Rejects when the server refuses the refresh (no cookie, revoked, expired).
 * @returns {Promise<string>} the new access token
 */
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    // Bare axios call so we don't recursively trigger the response interceptor.
    const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {},
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true });
    const newToken = data?.accessToken;
    if (!newToken) throw new Error('refresh response missing accessToken');
    setTokens({ accessToken: newToken });
    return newToken;
  })().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

/**
 * Resolve a currently-valid access token, refreshing first if the in-memory one is
 * missing or within the expiry skew window. Used by the live-sync WebSocket so
 * every (re)connect presents a non-expired JWT.
 * @returns {Promise<string>}
 */
export async function ensureFreshToken() {
  const token = getAccessToken();
  if (token && isAccessTokenFresh(token)) return token;
  return refreshAccessToken();
}

/**
 * Repopulate the in-memory access token on app start using the refresh cookie.
 * Resolves to the token, or null if the user has no valid session.
 */
export async function bootstrapAuth() {
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}

const isAuthPathToSkip = (url) => {
  if (!url) return false;
  return url.includes('/auth/refresh') ||
         url.includes('/auth/logout')  ||
         url.includes('/auth/signin')  ||
         url.includes('/auth/signup')  ||
         url.includes('/auth/forgot-password') ||
         url.includes('/auth/reset-password') ||
         url.includes('/auth/verify-otp') ||
         url.includes('/auth/resend-otp');
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Optimistic-locking conflict: surface as a typed error callers can catch.
    if (status === 409 && error.response?.data?.error === 'STALE') {
      return Promise.reject(new StaleDataError(error.response.data));
    }

    if (status !== 401 || !original || original._retry || isAuthPathToSkip(original.url)) {
      if (status === 401 && isAuthPathToSkip(original?.url)) {
        // Authentication endpoint itself returned 401 — no refresh possible.
        setSignOutReason('expired');
        clearTokens();
        if (typeof window !== 'undefined') window.location.href = '/signin';
      }
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      const newToken = await refreshAccessToken();
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      setSignOutReason('expired');
      clearTokens();
      if (typeof window !== 'undefined') window.location.href = '/signin';
      return Promise.reject(refreshErr);
    }
  },
);

// Storage helpers exported for AuthContext
export const tokenStorage = { getAccessToken, setTokens, clearTokens, onAccessTokenChange };

// Auth API
export const authAPI = {
  signUp: (data) => api.post('/auth/signup', data),
  getSignupStatus: () => api.get('/auth/signup-status'),
  signIn: (data) => api.post('/auth/signin', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (data) => api.post('/auth/resend-otp', data),
  refresh: () => api.post('/auth/refresh'),                 // cookie carries the refresh token
  logout:  () => api.post('/auth/logout'),                  // cookie carries the refresh token
  getCurrentUser: () => api.get('/auth/me'),
  getRoles: () => api.get('/auth/roles'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Admin API (signup-flag + pending-approval management + role management)
export const adminAPI = {
  getSignupFlag: () => api.get('/admin/settings/signup'),
  updateSignupFlag: (enabled) => api.put('/admin/settings/signup', { enabled }),
  listPendingUsers: () => api.get('/admin/users/pending'),
  approveUser: (id, role) => api.post(`/admin/users/${id}/approve`, { roleName: role }),
  // Role management
  listAllRoles: () => api.get('/admin/roles'),
  listAllPermissions: () => api.get('/admin/roles/permissions'),
  createRole: (data) => api.post('/admin/roles', data),
  updateRolePermissions: (roleName, data, config) => api.put(`/admin/roles/${encodeURIComponent(roleName)}/permissions`, data, config),
  deleteRole: (roleName) => api.delete(`/admin/roles/${encodeURIComponent(roleName)}`),
  assignRoleToUser: (userId, data) => api.post(`/admin/roles/users/${userId}/assign`, data),
  listAllUsers: () => api.get('/admin/roles/users'),
  rejectUser: (id) => api.post(`/admin/users/${id}/reject`),
  getUserPermissions: (userId) => api.get(`/admin/roles/users/${userId}/permissions`),
  updateUserPermissions: (userId, body) => api.put(`/admin/roles/users/${userId}/permissions`, body),
  // RBAC wizard APIs
  getModuleCatalog: () => api.get('/admin/modules'),
  getRoleConfig: (roleName) => api.get(`/admin/roles/${encodeURIComponent(roleName)}/config`),
  /**
   * Save a role's full permission set.
   * Mirrors the existing updateRolePermissions call: version passed as If-Match header.
   * @param {string} roleName
   * @param {string[]} permissionCodes
   * @param {number|null} version
   */
  saveRolePermissions: (roleName, permissionCodes, version) =>
    api.put(
      `/admin/roles/${encodeURIComponent(roleName)}/permissions`,
      { permissionCodes },
      withIfMatch(version),
    ),
  // Workflow routes
  listWorkflowRoutes: (documentType) =>
    api.get('/admin/workflow-routes', { params: documentType ? { documentType } : {} }),
  createWorkflowRoute: (body) => api.post('/admin/workflow-routes', body),
  updateWorkflowRoute: (id, body, version) => api.put(`/admin/workflow-routes/${id}`, body, withIfMatch(version)),
  deleteWorkflowRoute: (id) => api.delete(`/admin/workflow-routes/${id}`),
  // Module catalog (no-code editor)
  createModule: (data) => api.post('/admin/modules', data),
  updateModule: (code, data) => api.put(`/admin/modules/${encodeURIComponent(code)}`, data),
  deleteModule: (code) => api.delete(`/admin/modules/${encodeURIComponent(code)}`),
  setModuleActions: (code, actionCodes) =>
    api.put(`/admin/modules/${encodeURIComponent(code)}/actions`, { actionCodes }),
};

// Material Scope API — update/delete accept axios config so callers can pass withIfMatch(version)
export const materialScopeAPI = {
  // Disciplines
  getDisciplines: () => api.get('/material-scope/disciplines'),
  getActiveDisciplines: () => api.get('/material-scope/disciplines/active'),
  getDisciplineById: (id) => api.get(`/material-scope/disciplines/${id}`),
  createDiscipline: (data) => api.post('/material-scope/disciplines', data),
  updateDiscipline: (id, data, config) => api.put(`/material-scope/disciplines/${id}`, data, config),
  deleteDiscipline: (id, config) => api.delete(`/material-scope/disciplines/${id}`, config),

  // Materials
  getMaterials: () => api.get('/material-scope/materials'),
  getMaterialsByDiscipline: (disciplineId) => api.get(`/material-scope/materials/by-discipline/${disciplineId}`),
  getMaterialById: (id) => api.get(`/material-scope/materials/${id}`),
  createMaterial: (data) => api.post('/material-scope/materials', data),
  updateMaterial: (id, data, config) => api.put(`/material-scope/materials/${id}`, data, config),
  deleteMaterial: (id, config) => api.delete(`/material-scope/materials/${id}`, config),

  // Test Parameters
  getTestParameters: () => api.get('/material-scope/test-parameters'),
  getTestParametersByMaterial: (materialId) => api.get(`/material-scope/test-parameters/by-material/${materialId}`),
  getTestParameterById: (id) => api.get(`/material-scope/test-parameters/${id}`),
  createTestParameter: (data) => api.post('/material-scope/test-parameters', data),
  createBulkTestParameters: (data) => api.post('/material-scope/test-parameters/bulk', data),
  updateTestParameter: (id, data, config) => api.put(`/material-scope/test-parameters/${id}`, data, config),
  deleteTestParameter: (id, config) => api.delete(`/material-scope/test-parameters/${id}`, config),

  // Search
  searchDisciplines: (query) => api.get(`/material-scope/search/disciplines?query=${encodeURIComponent(query)}`),
  searchMaterials: (query) => api.get(`/material-scope/search/materials?query=${encodeURIComponent(query)}`),
  searchTestParameters: (query) => api.get(`/material-scope/search/test-parameters?query=${encodeURIComponent(query)}`),

  // Full Hierarchy
  getHierarchy: () => api.get('/material-scope/hierarchy'),
};

// Customer API
export const customerAPI = {
  registerCustomer: (data) => api.post('/customers/register', data),
  getAllCustomers: () => api.get('/customers'),
  // Paginated endpoint with server-side filtering and sorting
  getCustomersPaginated: ({ page = 0, size = 20, search = '', sortBy = 'createdAt', sortDir = 'desc' } = {}) => 
    api.get('/customers/paginated', { params: { page, size, search, sortBy, sortDir } }),
  getCustomerById: (id) => api.get(`/customers/${id}`),
  searchCustomers: (query) => api.get(`/customers/search?query=${encodeURIComponent(query)}`),
  updateCustomer: (id, data, config) => api.put(`/customers/${id}`, data, config),
  deleteCustomer: (id) => api.delete(`/customers/${id}`),
  getAdjacent: (id) => api.get(`/customers/${id}/adjacent`),
};

// Enquiry API
export const enquiryAPI = {
  createEnquiry: (data) => api.post('/enquiries', data),
  getAllEnquiries: () => api.get('/enquiries'),
  // Paginated endpoint with server-side filtering and sorting
  // Forwards every param (legacy + advanced filters); the backend ignores unknown ones.
  getEnquiriesPaginated: (params = {}) =>
    api.get('/enquiries/paginated', { params }),
  getEnquiryById: (id) => api.get(`/enquiries/${id}`),
  updateEnquiry: (id, data, config) => api.put(`/enquiries/${id}`, data, config),
  deleteEnquiry: (id) => api.delete(`/enquiries/${id}`),
  submitEnquiry: (id) => api.post(`/enquiries/${id}/submit`),
  approveEnquiry: (id) => api.post(`/enquiries/${id}/approve`),
  rejectEnquiry: (id, rejectionReason) => api.post(`/enquiries/${id}/reject`, { rejectionReason }),
  getAdjacent: (id, params = {}) => api.get(`/enquiries/${id}/adjacent`, { params }),
};

// Quotation API
export const quotationAPI = {
  createQuotation: (data) => api.post('/quotations', data),
  getAllQuotations: () => api.get('/quotations'),
  // Paginated endpoint with server-side filtering and sorting
  // Forwards every param (legacy + advanced filters); the backend ignores unknown ones.
  getQuotationsPaginated: (params = {}) =>
    api.get('/quotations/paginated', { params }),
  getQuotationById: (id) => api.get(`/quotations/${id}`),
  getQuotationsByThread: (threadId) => api.get(`/quotations/thread/${threadId}`),
  createRevision: (sourceId, data) => api.post(`/quotations/${sourceId}/revisions`, data),
  updateQuotation: (id, data, config) => api.put(`/quotations/${id}`, data, config),
  deleteQuotation: (id) => api.delete(`/quotations/${id}`),
  submitQuotation: (id) => api.post(`/quotations/${id}/submit`),
  approveQuotation: (id) => api.post(`/quotations/${id}/approve`),
  finalizeCustomerApproval: (id) => api.post(`/quotations/${id}/finalize-customer-approval`),
  rejectQuotation: (id, rejectionReason) => api.post(`/quotations/${id}/reject`, { rejectionReason }),
  downloadPdf: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),
  /**
   * Fetch the immutable-PDF checksum for a quotation. Returns null when the
   * quotation is still DRAFT (no snapshot yet).
   * @param {number|string} id
   * @returns {Promise<{sha256: string, fileSizeBytes: number, generatedAt: string, generatedBy: string} | null>}
   */
  fetchPdfChecksum: async (id) => {
    try {
      const { data } = await api.get(`/quotations/${id}/pdf/checksum`);
      return data?.data ?? null;
    } catch (e) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },
  /** Per-line testing allocation; optional excludeServiceRequestFormId when editing a draft SRF */
  getTestingAllocation: (id, { excludeServiceRequestFormId } = {}) =>
    api.get(`/quotations/${id}/testing-allocation`, {
      params: excludeServiceRequestFormId != null ? { excludeServiceRequestFormId } : {},
    }),
  /** Pre-built SRF line items from an approved quotation (business logic runs server-side). */
  getSrfDraftItems: (id, { excludeServiceRequestFormId } = {}) =>
    api.get(`/quotations/${id}/srf-draft-items`, {
      params: excludeServiceRequestFormId != null ? { excludeServiceRequestFormId } : {},
    }),
  getAdjacent: (id, params = {}) => api.get(`/quotations/${id}/adjacent`, { params }),
};

// Utility API
export const utilityAPI = {
  // Payment Terms
  getPaymentTerms: () => api.get('/payment-terms'),
  getPaymentTermById: (id) => api.get(`/payment-terms/${id}`),
  createPaymentTerm: (data) => api.post('/payment-terms', data),
  updatePaymentTerm: (id, data, config) => api.put(`/payment-terms/${id}`, data, config),
  deletePaymentTerm: (id, config) => api.delete(`/payment-terms/${id}`, config),

  // Responsibility Matrices — update/delete accept axios config so callers can pass withIfMatch(version)
  getResponsibilityMatrices: () => api.get('/responsibility-matrices'),
  getResponsibilityMatrixById: (id) => api.get(`/responsibility-matrices/${id}`),
  createResponsibilityMatrix: (data) => api.post('/responsibility-matrices', data),
  updateResponsibilityMatrix: (id, data, config) => api.put(`/responsibility-matrices/${id}`, data, config),
  deleteResponsibilityMatrix: (id, config) => api.delete(`/responsibility-matrices/${id}`, config),

  // Terms and Conditions
  getTermsAndConditions: () => api.get('/terms-and-conditions'),
  getTermsAndConditionById: (id) => api.get(`/terms-and-conditions/${id}`),
  createTermsAndCondition: (data) => api.post('/terms-and-conditions', data),
  updateTermsAndCondition: (id, data, config) => api.put(`/terms-and-conditions/${id}`, data, config),
  deleteTermsAndCondition: (id, config) => api.delete(`/terms-and-conditions/${id}`, config),

  // Bank Details
  getBankDetails: () => api.get('/bank-details'),
  getBankDetailById: (id) => api.get(`/bank-details/${id}`),
  createBankDetail: (data) => api.post('/bank-details', data),
  updateBankDetail: (id, data, config) => api.put(`/bank-details/${id}`, data, config),
  deleteBankDetail: (id, config) => api.delete(`/bank-details/${id}`, config),
};

// Reference / master data API
export const referenceAPI = {
  getIndianStates: () => api.get('/reference/indian-states'),
};

// Service Request Form API
export const serviceRequestFormAPI = {
  previewNextJobId: () => api.get('/service-request-forms/next-job-id'),
  createServiceRequestForm: (data) => api.post('/service-request-forms', data),
  /** Customer + office copies in one request (linked pair) */
  createPairedServiceRequestForms: (data) => api.post('/service-request-forms/paired', data),
  /** Atomic update of both paired copies; data wraps the shared payload with per-copy versions */
  updatePairedServiceRequestForms: (anyCopyId, data) =>
    api.put(`/service-request-forms/${anyCopyId}/paired`, data),
  getAllServiceRequestForms: () => api.get('/service-request-forms'),
  /** APPROVED SRFs that don't already have an active (non-cancelled) PI. */
  getApprovedSrfsWithoutActivePi: () =>
    api.get('/service-request-forms/approved-without-active-pi'),
  // Forwards every param (legacy + advanced filters); the backend ignores unknown ones.
  getServiceRequestFormsPaginated: (params = {}) =>
    api.get('/service-request-forms/paginated', { params }),
  getServiceRequestFormById: (id) => api.get(`/service-request-forms/${id}`),
  updateServiceRequestForm: (id, data, config) => api.put(`/service-request-forms/${id}`, data, config),
  deleteServiceRequestForm: (id) => api.delete(`/service-request-forms/${id}`),
  submitServiceRequestForm: (id) => api.post(`/service-request-forms/${id}/submit`),
  approveServiceRequestForm: (id) => api.post(`/service-request-forms/${id}/approve`),
  rejectServiceRequestForm: (id, rejectionReason) => api.post(`/service-request-forms/${id}/reject`, { rejectionReason }),
  downloadPdf: (id) => api.get(`/service-request-forms/${id}/pdf`, { responseType: 'blob' }),
  getAdjacent: (id, params = {}) => api.get(`/service-request-forms/${id}/adjacent`, { params }),
};

export const materialInwardAPI = {
  getBySrf: (srfId) => api.get(`/service-request-forms/${srfId}/material-inwards`),
  create: (srfId, data) => api.post(`/service-request-forms/${srfId}/material-inwards`, data),
  update: (srfId, inwardId, data) => api.put(`/service-request-forms/${srfId}/material-inwards/${inwardId}`, data),
};

export const userAPI = {
  getTestingEngineers: () => api.get('/users/testing-engineers'),
};

// Proforma Invoice API
export const proformaInvoiceAPI = {
  createInvoice: (data) => api.post('/proforma-invoices', data),
  getAllInvoices: () => api.get('/proforma-invoices'),
  // Paginated endpoint with server-side filtering and sorting
  getInvoicesPaginated: ({ page = 0, size = 20, search = '', status = '', sortBy = 'createdAt', sortDir = 'desc' } = {}) =>
    api.get('/proforma-invoices/paginated', { params: { page, size, search, status, sortBy, sortDir } }),
  getInvoiceById: (id) => api.get(`/proforma-invoices/${id}`),
  getAdjacent: (id) => api.get(`/proforma-invoices/${id}/adjacent`),
  draftFromServiceRequestForm: (serviceRequestFormId) =>
    api.get(`/proforma-invoices/draft-from-service-request-form/${serviceRequestFormId}`),
  /** Non-locking read: returns active PI for SRF (v2). */
  activeBySrf: (srfId) => api.get(`/proforma-invoices/active-by-srf/${srfId}`),
  updateInvoice: (id, data, config) => api.put(`/proforma-invoices/${id}`, data, config),
  deleteInvoice: (id) => api.delete(`/proforma-invoices/${id}`),
  submitInvoice: (id) => api.post(`/proforma-invoices/${id}/submit`),
  approveInvoice: (id) => api.post(`/proforma-invoices/${id}/approve`),
  rejectInvoice: (id, rejectionReason) => api.post(`/proforma-invoices/${id}/reject`, { rejectionReason }),
  markPaymentReceived: (id) => api.post(`/proforma-invoices/${id}/payment-received`),
  downloadPdf: (id) => api.get(`/proforma-invoices/${id}/pdf`, { responseType: 'blob' }),
  /** Approved PIs not yet converted to a customer (final) invoice */
  getApprovedNotInvoiced: ({ page = 0, size = 100, sortBy = 'createdAt', sortDir = 'desc' } = {}) =>
    api.get('/proforma-invoices/approved-not-invoiced', { params: { page, size, sortBy, sortDir } }),
};

// Customer invoices (tax invoices / bills) created from approved proforma invoices
export const finalInvoiceAPI = {
  getPaginated: ({ page = 0, size = 20, search = '', sortBy = 'createdAt', sortDir = 'desc' } = {}) =>
    api.get('/final-invoices/paginated', { params: { page, size, search, sortBy, sortDir } }),
  getById: (id) => api.get(`/final-invoices/${id}`),
  getAdjacent: (id) => api.get(`/final-invoices/${id}/adjacent`),
  createFromProforma: (data) => api.post('/final-invoices/from-proforma', data),
  markPaid: (id) => api.post(`/final-invoices/${id}/mark-paid`),
  getPayments: (id) => api.get(`/final-invoices/${id}/payments`),
  addPayment: (id, data) => api.post(`/final-invoices/${id}/payments`, data),
  /** Soft-delete a payment entry; reason is required (Plan D finding Q14). */
  deletePayment: (paymentId, reason) =>
    api.delete(`/final-invoice-payments/${paymentId}`, { params: { reason } }),
  updateTds: (id, data, config = {}) => api.patch(`/final-invoices/${id}/tds`, data, config),
  updateLineDescriptions: (id, data, config = {}) =>
    api.patch(`/final-invoices/${id}/line-descriptions`, data, config),
  delete: (id) => api.delete(`/final-invoices/${id}`),
  downloadPdf: (id) => api.get(`/final-invoices/${id}/pdf`, { responseType: 'blob' }),
  /** Cancel an ISSUED or PARTIALLY_PAID invoice (Plan D finding F). */
  cancel: (id, reason) => api.post(`/final-invoices/${id}/cancel`, { reason }),
  /** Issue a credit note against a PAID invoice (Plan D finding F). */
  issueCreditNote: (id, creditNoteId, reason) =>
    api.post(`/final-invoices/${id}/credit-note`, { creditNoteId, reason }),
  /** Record send intent + recipient audit (Plan D findings Q15, Q16). */
  send: (id, recipients) => api.post(`/final-invoices/${id}/send`, { recipients }),
  /**
   * Fetch the immutable-PDF checksum for a final invoice (Plan H).
   * @param {number|string} id
   * @returns {Promise<{sha256: string, fileSizeBytes: number, generatedAt: string, generatedBy: string} | null>}
   */
  fetchPdfChecksum: async (id) => {
    try {
      const { data } = await api.get(`/final-invoices/${id}/pdf/checksum`);
      return data?.data ?? null;
    } catch (e) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },
};

// Global Search API
export const searchAPI = {
  globalSearch: (query, limit = 5) =>
    api.get('/search', { params: { query, limit } }),
};

// Dashboard live stats
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getMaterialMix: (window) => api.get('/dashboard/material-mix', { params: { window } }),
};

// Presence admin API — read/kick live sessions; gated server-side to ADMIN/CEO.
export const presenceAPI = {
  list: () => api.get('/admin/presence'),
  history: (params) => api.get('/admin/presence/history', { params }),
  kick: (sessionKey, reason) =>
    api.post(`/admin/presence/${encodeURIComponent(sessionKey)}/kick`, { reason }),
  metrics: () => api.get('/admin/presence/metrics'),
};

/**
 * Builds an axios request config with an If-Match header for optimistic locking.
 * Pass the entity's current `version` as a third arg to PUT/PATCH calls.
 */
export function withIfMatch(version) {
  if (version == null) return {};
  return { headers: { 'If-Match': `"${version}"` } };
}

/**
 * Extracts the numeric version from the ETag header of an axios response.
 * Handles both quoted ("6") and weak (W/"6") ETag forms.
 * Returns the parsed integer version, or null if absent / unparseable.
 *
 * NOTE: The ETag header is exposed only when the backend sends
 * `Access-Control-Expose-Headers: ETag` (wired in Phase 1 / Phase 3 scope).
 * In dev/Docker it is already present; production requires Phase 3 task 3.1.
 *
 * @param {import('axios').AxiosResponse} response
 * @returns {number|null}
 */
export function extractETagVersion(response) {
  const etag = response?.headers?.etag;
  if (!etag) return null;
  const parsed = Number(etag.replace(/^W\//, '').replace(/^"|"$/g, ''));
  if (!Number.isFinite(parsed)) {
    console.warn('[extractETagVersion] Malformed ETag header received:', etag);
    return null;
  }
  return parsed;
}

/**
 * Filter option loaders — return { value, label }[] arrays for the advanced-filter panel.
 * Reuse existing API functions; no new endpoints introduced.
 */
export const filterOptionsAPI = {
  disciplines: () =>
    materialScopeAPI.getDisciplines().then((r) =>
      (r.data.data || []).map((d) => ({ value: String(d.id), label: d.name }))
    ),
  materials: () =>
    materialScopeAPI.getMaterials().then((r) =>
      (r.data.data || []).map((m) => ({ value: String(m.id), label: m.name }))
    ),
  testParameters: () =>
    materialScopeAPI.getTestParameters().then((r) =>
      (r.data.data || []).map((t) => ({ value: String(t.id), label: t.parameterName }))
    ),
  customers: () =>
    customerAPI.getAllCustomers().then((r) =>
      (r.data.data || []).map((c) => ({ value: String(c.id), label: c.customerName }))
    ),
  /**
   * Admin/CEO only — always gate the call site with isAdmin before invoking (security M-4).
   * The /admin/roles/users endpoint also enforces this server-side.
   */
  users: () =>
    adminAPI.listAllUsers().then((r) =>
      (r.data.data || []).map((u) => ({ value: String(u.id), label: u.fullName || u.email }))
    ),
};

export default api;

